import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FlaskConical,
  Clock,
  User,
  Landmark,
  FileText,
  ShieldCheck,
  Plus,
  Trash2,
  Upload,
  MapPin,
  Loader2,
  Check,
  X,
  Crosshair,
  Search,
  Save,
  RotateCcw,
  Send,
  FileCheck,
  ChevronLeft,
  Pencil,
} from "lucide-react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import ccOnboardingApi, { currentUserCode, currentUserName } from "../../services/ccOnboardingApi";
import labOnboardingApi from "../../services/labOnboardingApi";
import { updateOnboarding, resubmitCentre } from "../../store/ccOnboardingSlice";
import { SearchableSelect } from "./ccOnboarding";
import ApprovalHistoryTable, { extractApprovalHistory } from "./ApprovalHistoryTable";
import apiService, { viewGcpDocument } from "../../services/actions";
import {
  setBasicField,
  setBasicFields,
  setDocument,
  deleteDocument,
  setTimings,
  addTimingSlot,
  updateTimingSlot,
  removeTimingSlot,
  addContact,
  updateContact,
  removeContact,
  addTestCity,
  updateTestCity,
  removeTestCity,
  setBankField,
  setNotes,
  setRemarks,
  setDraftKey,
  saveDraft,
  resetForm,
  setSubmitStatus,
  saveLabDraftServer,
  resumeLabDraftServer,
  submitLabDraftServer,
} from "../../store/labOnboardingSlice";

/* ------------------------------------------------------------------ *
 *  Indian States List
 * ------------------------------------------------------------------ */
const INDIAN_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry",
];

const STATE_CODE_MAP = {
  Odisha: "OD",
  "Tamil Nadu": "TN",
  Karnataka: "KA",
  Maharashtra: "MH",
  Delhi: "DL",
  Telangana: "TS",
  "West Bengal": "WB",
  "Andhra Pradesh": "AP",
  Bihar: "BR",
  Gujarat: "GJ",
  Haryana: "HR",
  "Madhya Pradesh": "MP",
  Punjab: "PB",
  Rajasthan: "RJ",
  "Uttar Pradesh": "UP",
  Kerala: "KL",
  Assam: "AS",
  Jharkhand: "JH",
  Chhattisgarh: "CG",
  Uttarakhand: "UK",
  "Himachal Pradesh": "HP",
  Tripura: "TR",
  Meghalaya: "ML",
  Manipur: "MN",
  Nagaland: "NL",
  Goa: "GA",
  "Arunachal Pradesh": "AR",
  Mizoram: "MZ",
  Sikkim: "SK",
  "Jammu and Kashmir": "JK",
  Ladakh: "LA",
  Puducherry: "PY",
  Chandigarh: "CH",
};

export function resolveStateCode(stateVal, rawStateList = []) {
  if (!stateVal) return "";
  const sStr = String(stateVal).trim();
  if (sStr.length <= 3 && sStr === sStr.toUpperCase()) {
    return sStr;
  }
  const match = (rawStateList || []).find(
    (s) =>
      s &&
      (s.stateCode === sStr ||
        s.code === sStr ||
        s.stateName?.toLowerCase() === sStr.toLowerCase() ||
        s.name?.toLowerCase() === sStr.toLowerCase())
  );
  if (match) {
    return match.stateCode || match.code || match.id || sStr;
  }
  return STATE_CODE_MAP[sStr] || sStr;
}

function useStateOptions() {
  const [states, setStates] = useState(INDIAN_STATES);
  const [rawStates, setRawStates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    ccOnboardingApi
      .getStateList()
      .then((res) => {
        const list = res?.data?.data || res?.data || [];
        const raw = Array.isArray(list) ? list : [];
        if (active) setRawStates(raw);
        const names = raw
          .map((s) => (typeof s === "string" ? s : s.stateName ?? s.name ?? s.label))
          .filter(Boolean);
        if (active && names.length) setStates(names);
      })
      .catch(() => {
        /* keep static fallback */
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return { states, rawStates, loading };
}

/* ------------------------------------------------------------------ *
 *  Constants matching CC Onboarding & Postman collection
 * ------------------------------------------------------------------ */
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DAY_SHORT = {
  Monday: "Mon",
  Tuesday: "Tue",
  Wednesday: "Wed",
  Thursday: "Thu",
  Friday: "Fri",
  Saturday: "Sat",
  Sunday: "Sun",
};

const PAYMENT_PERIOD_OPTIONS = [
  { label: "7 days", value: "weekly" },
  { label: "15 Days", value: "biweekly" },
  { label: "30 Days", value: "monthly" },
];

const PAYMENT_RECONCILIATION_OPTIONS = [
  { label: "7 Days", value: "7" },
  { label: "15 Days", value: "15" },
  { label: "30 Days", value: "30" },
  { label: "45 Days", value: "45" },
];

const LAB_TYPE_OPTIONS = [
  { label: "Pathology", value: "pathology" },
  { label: "Radiology", value: "radiology" },
];

const SALUTATION_OPTIONS = ["Mr.", "Ms.", "Mrs.", "Dr."];
const GENDER_OPTIONS = ["Male", "Female", "Other"];
const SOURCE_OPTIONS = ["Advertisement", "Campaign", "Others", "Referral", "Social Media", "Website"];
const QUALIFICATION_OPTIONS = ["10th Pass", "12th Pass", "Graduate", "Post Graduate"];
const CUREBAY_LAB_OPTIONS = [
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" },
];

/* ------------------------------------------------------------------ *
 *  Form Control Atoms (Single Column & CC Uppercase Labels)
 * ------------------------------------------------------------------ */
function Field({ label, required, children, className = "", helperText = "" }) {
  return (
    <div className={`w-full text-left ${className}`}>
      <label className="mb-1.5 block text-left text-xs font-semibold uppercase tracking-wide text-slate-700">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      {children}
      {helperText && <p className="mt-1 text-left text-xs text-slate-400">{helperText}</p>}
    </div>
  );
}

const inputCls =
  "w-full rounded-lg border border-slate-400 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed";

function TextInput(props) {
  return <input {...props} className={`${inputCls} ${props.className || ""}`} />;
}

function TextArea(props) {
  return <textarea {...props} className={`${inputCls} resize-none ${props.className || ""}`} />;
}

function DateInput({ value, onChange, disabled = false, maxDate, minDate, placeholder = "YYYY-MM-DD" }) {
  const dayjsValue = value ? dayjs(value) : null;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        value={dayjsValue}
        disabled={disabled}
        maxDate={maxDate ? dayjs(maxDate) : undefined}
        minDate={minDate ? dayjs(minDate) : undefined}
        format="YYYY-MM-DD"
        onChange={(newDate) => {
          if (newDate && dayjs.isDayjs(newDate) && newDate.isValid()) {
            onChange(newDate.format("YYYY-MM-DD"));
          } else {
            onChange("");
          }
        }}
        slotProps={{
          textField: {
            size: "small",
            fullWidth: true,
            placeholder: placeholder,
            sx: {
              "& .MuiOutlinedInput-root": {
                borderRadius: "0.5rem",
                backgroundColor: disabled ? "#f8fafc" : "#ffffff",
                fontSize: "0.875rem",
                fontFamily: "inherit",
                "& fieldset": {
                  borderColor: "#94a3b8",
                },
                "&:hover fieldset": {
                  borderColor: "#6366f1",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#4f46e5",
                  borderWidth: "1.5px",
                },
              },
              "& .MuiInputBase-input": {
                padding: "9px 12px",
                fontSize: "0.875rem",
                color: "#0f172a",
                fontFamily: "inherit",
              },
            },
          },
        }}
      />
    </LocalizationProvider>
  );
}

/* ------------------------------------------------------------------ *
 *  FileInput matching CC Onboarding Exactly
 * ------------------------------------------------------------------ */
function FileInput({
  file,
  remoteUrl,
  remoteName,
  hint,
  readOnly = false,
  onChange,
  onDelete,
  onPreview,
}) {
  const inputId = useMemo(() => `file-${Math.random().toString(36).slice(2)}`, []);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return undefined;
    }
    if (file instanceof File) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else if (file.previewUrl || file.url) {
      setPreviewUrl(file.previewUrl || file.url);
    }
  }, [file]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    const url = URL.createObjectURL(selectedFile);
    if (onChange) {
      onChange({
        name: selectedFile.name,
        size: selectedFile.size,
        type: selectedFile.type,
        previewUrl: url,
        file: selectedFile,
      });
    }
    e.target.value = "";
  };

  const handleDelete = () => {
    if (onDelete) onDelete();
  };

  const hasFile = Boolean(file || remoteUrl);
  const fileName = file?.name || remoteName || (typeof file === "string" ? file : "Uploaded document");
  const activeUrl = previewUrl || remoteUrl || file?.previewUrl || file?.url;

  const handleView = () => {
    if (!activeUrl) return;
    if (onPreview) {
      onPreview({ name: fileName, url: activeUrl, previewUrl: activeUrl });
      return;
    }
    if (activeUrl.startsWith("blob:") || activeUrl.startsWith("data:") || activeUrl.includes("X-Goog-Signature")) {
      window.open(activeUrl, "_blank");
      return;
    }
    viewGcpDocument(activeUrl, (err) => toast.error(err));
  };

  if (hasFile) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-slate-400 bg-slate-50 px-3 py-2 text-sm w-full">
        <Upload className="h-4 w-4 shrink-0 text-slate-400" />
        <span className="flex-1 truncate text-slate-700 font-medium text-xs">
          {fileName}
        </span>
        {activeUrl && (
          <button
            type="button"
            onClick={handleView}
            className="flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-indigo-600 hover:bg-indigo-50 transition shadow-2xs cursor-pointer"
          >
            View
          </button>
        )}
        {!readOnly && (
          <>
            <label
              htmlFor={inputId}
              className="cursor-pointer rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 transition shadow-2xs"
            >
              Replace
            </label>
            <button
              type="button"
              onClick={handleDelete}
              className="flex items-center gap-1 rounded-md border border-rose-200 bg-white px-2 py-1 text-xs font-medium text-rose-600 hover:bg-rose-50 transition shadow-2xs"
              title="Delete upload"
            >
              <X className="h-3.5 w-3.5" /> Delete
            </button>
            <input id={inputId} type="file" className="hidden" onChange={handleFileChange} />
          </>
        )}
      </div>
    );
  }

  if (readOnly) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-slate-300 bg-slate-50 px-3 py-2.5 text-sm text-slate-400 italic w-full">
        No document uploaded
      </div>
    );
  }

  return (
    <label
      htmlFor={inputId}
      className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-400 bg-slate-50 px-3 py-2.5 text-sm text-slate-500 transition hover:border-indigo-400 hover:bg-indigo-50/50 w-full"
    >
      <Upload className="h-4 w-4 shrink-0 text-slate-400" />
      <span className="truncate text-xs text-slate-500">{hint || "Click to upload file"}</span>
      <input id={inputId} type="file" className="hidden" onChange={handleFileChange} />
    </label>
  );
}

/* ------------------------------------------------------------------ *
 *  Document Preview Modal
 * ------------------------------------------------------------------ */
function DocumentPreviewModal({ doc, onClose }) {
  const [resolvedUrl, setResolvedUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const rawUrl = doc?.previewUrl || doc?.url;

  useEffect(() => {
    if (!rawUrl) {
      setResolvedUrl(null);
      return;
    }
    if (rawUrl.startsWith("blob:") || rawUrl.startsWith("data:") || rawUrl.includes("X-Goog-Signature")) {
      setResolvedUrl(rawUrl);
      return;
    }

    let isMounted = true;
    setLoading(true);
    setError(null);

    apiService.getDocumentUrl(rawUrl)
      .then((signedUrl) => {
        if (!isMounted) return;
        if (signedUrl) {
          setResolvedUrl(signedUrl);
        } else {
          setError("Failed to load secure document preview. File may not be available in storage.");
        }
      })
      .catch((err) => {
        if (!isMounted) return;
        console.error("Error resolving document preview URL:", err);
        setError("Error loading document preview.");
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [rawUrl]);

  if (!doc) return null;

  const displayUrl = resolvedUrl;
  const isImage =
    rawUrl?.startsWith("data:image") ||
    doc.name?.match(/\.(jpeg|jpg|gif|png|webp)$/i) ||
    rawUrl?.match(/\.(jpeg|jpg|gif|png|webp)($|\?)/i) ||
    displayUrl?.match(/\.(jpeg|jpg|gif|png|webp)($|\?)/i);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm">
      <div className="relative flex max-h-[90vh] w-full max-w-4xl flex-col rounded-2xl bg-white shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div className="flex items-center gap-2">
            <FileCheck className="h-5 w-5 text-indigo-600" />
            <h3 className="text-sm font-semibold text-slate-800 truncate max-w-md">
              {doc.title || doc.name || "Document Preview"}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            {displayUrl && (
              <a
                href={displayUrl}
                target="_blank"
                rel="noreferrer"
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 transition"
              >
                Open in new tab
              </a>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition cursor-pointer"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto bg-slate-100 p-6 flex items-center justify-center min-h-[400px]">
          {loading ? (
            <div className="flex flex-col items-center gap-2 text-slate-500 text-sm">
              <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
              <span>Loading secure document preview...</span>
            </div>
          ) : error ? (
            <div className="text-sm text-rose-600 font-medium text-center p-4 bg-rose-50 border border-rose-200 rounded-lg max-w-md">
              {error}
            </div>
          ) : isImage ? (
            <img
              src={displayUrl}
              alt={doc.name}
              className="max-h-[70vh] max-w-full rounded-lg object-contain shadow-md"
            />
          ) : displayUrl ? (
            <iframe
              src={displayUrl}
              title={doc.name || "Document"}
              className="h-[70vh] w-full rounded-lg border border-slate-300 bg-white shadow-sm"
            />
          ) : (
            <div className="text-xs text-slate-400">No document to display</div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 *  Interactive Location Map Modal with Reverse Geocoding (Exact CC)
 * ------------------------------------------------------------------ */
function LocationMapModal({ initialValue, initialCoords, onClose, onConfirm }) {
  const parsed = useMemo(() => {
    if (
      initialCoords &&
      !isNaN(initialCoords.lat) &&
      !isNaN(initialCoords.lng) &&
      Number(initialCoords.lat) !== 0 &&
      Number(initialCoords.lng) !== 0
    ) {
      return { lat: parseFloat(initialCoords.lat), lng: parseFloat(initialCoords.lng) };
    }
    const m = /(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/.exec(initialValue || "");
    return m ? { lat: parseFloat(m[1]), lng: parseFloat(m[2]) } : { lat: 20.3558, lng: 85.8168 };
  }, [initialValue, initialCoords]);

  const [coords, setCoords] = useState(parsed);
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [addressPreview, setAddressPreview] = useState(null);
  const [fetchingAddress, setFetchingAddress] = useState(false);

  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const searchContainerRef = useRef(null);

  const fetchAddressDetails = useCallback(async (lat, lng) => {
    setFetchingAddress(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`
      );
      if (response.ok) {
        const data = await response.json();
        const addr = data.address || {};
        const areaName =
          addr.suburb ||
          addr.neighbourhood ||
          addr.residential ||
          addr.road ||
          addr.village ||
          addr.town ||
          addr.city ||
          "";
        const parsedData = {
          address: data.display_name || "",
          location: areaName,
          area: areaName,
          city: addr.city || addr.town || addr.village || addr.county || addr.state_district || "",
          state: addr.state || "",
          pinCode: addr.postcode || "",
          latitude: lat,
          longitude: lng,
        };
        setAddressPreview(parsedData);
        return parsedData;
      }
    } catch {
      // ignore
    } finally {
      setFetchingAddress(false);
    }
    return null;
  }, []);

  const panToCoords = useCallback((lat, lng, zoom = 16) => {
    setCoords({ lat, lng });
    if (mapInstanceRef.current && markerRef.current) {
      mapInstanceRef.current.setView([lat, lng], zoom);
      markerRef.current.setLatLng([lat, lng]);
    }
    fetchAddressDetails(lat, lng);
  }, [fetchAddressDetails]);

  useEffect(() => {
    let active = true;

    const loadLeaflet = async () => {
      if (!window.L) {
        if (!document.getElementById("leaflet-css")) {
          const link = document.createElement("link");
          link.id = "leaflet-css";
          link.rel = "stylesheet";
          link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
          document.head.appendChild(link);
        }

        if (!document.getElementById("leaflet-js")) {
          const script = document.createElement("script");
          script.id = "leaflet-js";
          script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
          document.body.appendChild(script);
          await new Promise((resolve) => {
            script.onload = resolve;
          });
        } else {
          while (!window.L) {
            await new Promise((r) => setTimeout(r, 100));
          }
        }
      }

      if (!active || !mapContainerRef.current) return;
      const L = window.L;
      if (!L) return;

      if (!mapInstanceRef.current && mapContainerRef.current) {
        const initialZoom = initialValue ? 15 : 5;
        const map = L.map(mapContainerRef.current).setView([coords.lat, coords.lng], initialZoom);
        mapInstanceRef.current = map;

        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution: "&copy; OpenStreetMap contributors",
          maxZoom: 19,
        }).addTo(map);

        const customIcon = L.icon({
          iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
          shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowSize: [41, 41],
        });

        const marker = L.marker([coords.lat, coords.lng], {
          draggable: true,
          icon: customIcon,
        }).addTo(map);
        markerRef.current = marker;

        marker.on("dragend", (e) => {
          const latlng = e.target.getLatLng();
          const lat = parseFloat(latlng.lat.toFixed(6));
          const lng = parseFloat(latlng.lng.toFixed(6));
          setCoords({ lat, lng });
          fetchAddressDetails(lat, lng);
        });

        map.on("click", (e) => {
          const lat = parseFloat(e.latlng.lat.toFixed(6));
          const lng = parseFloat(e.latlng.lng.toFixed(6));
          marker.setLatLng([lat, lng]);
          setCoords({ lat, lng });
          fetchAddressDetails(lat, lng);
        });

        setTimeout(() => {
          map.invalidateSize();
        }, 200);

        if (initialValue) {
          fetchAddressDetails(coords.lat, coords.lng);
        }
      }
    };

    loadLeaflet();

    return () => {
      active = false;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        markerRef.current = null;
      }
    };
  }, [coords.lat, coords.lng, fetchAddressDetails, initialValue]);

  useEffect(() => {
    if (!searchQuery.trim() || searchQuery.trim().length < 3) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
            searchQuery.trim()
          )}&limit=5&addressdetails=1`
        );
        if (res.ok) {
          const data = await res.json();
          setSearchResults(data || []);
        }
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setSearchResults([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectSearchResult = (item) => {
    const lat = parseFloat(parseFloat(item.lat).toFixed(6));
    const lng = parseFloat(parseFloat(item.lon).toFixed(6));
    panToCoords(lat, lng, 16);
    setSearchQuery("");
    setSearchResults([]);
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported by this browser.");
      return;
    }
    setLocating(true);
    setError("");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = parseFloat(pos.coords.latitude.toFixed(6));
        const lng = parseFloat(pos.coords.longitude.toFixed(6));
        panToCoords(lat, lng, 16);
        setLocating(false);
      },
      () => {
        setError("Could not fetch location. Please check browser permissions or search by location name.");
        setLocating(false);
      }
    );
  };

  const handleConfirm = async () => {
    const locStr = `${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`;
    const addressData = addressPreview || (await fetchAddressDetails(coords.lat, coords.lng));
    onConfirm(locStr, addressData, coords);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-2xl bg-white p-5 shadow-2xl">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h3 className="flex items-center gap-2 text-[15px] font-bold text-slate-900">
              <MapPin className="h-4 w-4 text-indigo-600" /> Pin &amp; Select Location on Map
            </h3>
            <p className="text-xs text-slate-500">
              Search a place, drag the pin, or click anywhere on the map to set coordinates.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Location Search Bar */}
        <div className="relative z-50 mb-3" ref={searchContainerRef}>
          <div className="relative flex items-center">
            <Search className="absolute left-3 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by location, area, city or landmark…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-10 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            />
            {searching ? (
              <Loader2 className="absolute right-3 h-4 w-4 animate-spin text-indigo-600" />
            ) : searchQuery ? (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery("");
                  setSearchResults([]);
                }}
                className="absolute right-3 text-slate-400 hover:text-slate-600"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>

          {/* Search Results Dropdown */}
          {searchResults.length > 0 && (
            <div className="absolute left-0 top-full z-[1001] mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-2xl">
              {searchResults.map((item) => (
                <div
                  key={item.place_id}
                  onClick={() => handleSelectSearchResult(item)}
                  className="flex cursor-pointer items-start gap-2.5 border-b border-slate-100 p-2.5 text-xs text-slate-700 transition hover:bg-indigo-50 hover:text-indigo-900 last:border-none"
                >
                  <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-indigo-500" />
                  <span className="line-clamp-2 leading-relaxed">{item.display_name}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Interactive Map Container */}
        <div className="relative z-0 h-72 w-full overflow-hidden rounded-xl border border-slate-300 bg-slate-100">
          <div ref={mapContainerRef} className="h-full w-full" />
          <div className="pointer-events-none absolute bottom-2 left-2 z-[400] rounded-md bg-white/90 px-2 py-1 text-[11px] font-medium text-slate-700 shadow backdrop-blur-sm">
            📍 Drag pin or click map to reposition
          </div>
        </div>

        {/* Action Controls & Lat/Lng Inputs */}
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={useCurrentLocation}
            disabled={locating}
            className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 transition hover:bg-indigo-100 disabled:opacity-60"
          >
            {locating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Crosshair className="h-3.5 w-3.5" />}
            Use current location
          </button>

          <div className="flex items-center gap-2 text-xs text-slate-600">
            <span className="font-semibold">Lat:</span>
            <input
              type="number"
              step="any"
              value={coords.lat}
              onChange={(e) => {
                const lat = parseFloat(e.target.value) || 0;
                panToCoords(lat, coords.lng);
              }}
              className="w-24 rounded border border-slate-300 px-2 py-1 text-xs outline-none focus:border-indigo-400"
            />
            <span className="font-semibold">Lng:</span>
            <input
              type="number"
              step="any"
              value={coords.lng}
              onChange={(e) => {
                const lng = parseFloat(e.target.value) || 0;
                panToCoords(coords.lat, lng);
              }}
              className="w-24 rounded border border-slate-300 px-2 py-1 text-xs outline-none focus:border-indigo-400"
            />
          </div>
        </div>

        {/* Address Preview Box */}
        {(addressPreview || fetchingAddress) && (
          <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-2.5 text-xs text-slate-700">
            <div className="flex items-center justify-between font-semibold text-slate-900">
              <span>Captured Address Details:</span>
              {fetchingAddress && (
                <span className="flex items-center gap-1 text-[11px] font-normal text-indigo-600">
                  <Loader2 className="h-3 w-3 animate-spin" /> Fetching address…
                </span>
              )}
            </div>
            {addressPreview && (
              <div className="mt-1 space-y-0.5">
                <div className="line-clamp-2 text-slate-600">{addressPreview.address}</div>
                <div className="text-[11px] text-slate-500">
                  {[addressPreview.city, addressPreview.state, addressPreview.pinCode].filter(Boolean).join(" • ")}
                </div>
              </div>
            )}
          </div>
        )}

        {error && <p className="mt-2 text-xs text-rose-500">{error}</p>}

        <div className="mt-4 flex justify-end gap-2.5">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-xs font-bold text-white shadow-sm transition hover:bg-indigo-700"
          >
            Confirm Location &amp; Address
          </button>
        </div>
      </div>
    </div>
  );
}

function LocationPickerField({ value, latitude, longitude, onConfirmLocation, disabled = false }) {
  const [open, setOpen] = useState(false);
  const displayLabel = useMemo(() => {
    const lat = latitude !== undefined && latitude !== null ? String(latitude).trim() : "";
    const lng = longitude !== undefined && longitude !== null ? String(longitude).trim() : "";
    if (lat && lng) return `${lat}, ${lng}`;
    if (value && /(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/.test(value)) {
      return value;
    }
    return "";
  }, [value, latitude, longitude]);

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        className={`flex w-full items-center gap-2 rounded-lg border border-slate-400 bg-white px-3 py-2.5 text-left text-sm outline-none transition hover:border-indigo-400 hover:bg-indigo-50/40 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed ${
          displayLabel ? "text-slate-800 font-medium" : "text-slate-400"
        }`}
      >
        <MapPin className="h-4 w-4 shrink-0 text-indigo-600" />
        <span className="truncate">{displayLabel || "Select location on map"}</span>
      </button>
      {open && (
        <LocationMapModal
          initialValue={displayLabel}
          initialCoords={latitude && longitude ? { lat: Number(latitude), lng: Number(longitude) } : null}
          onClose={() => setOpen(false)}
          onConfirm={(locStr, addressData, coords) => {
            onConfirmLocation({ latLngStr: locStr, addressData, lat: coords.lat, lng: coords.lng });
            setOpen(false);
          }}
        />
      )}
    </>
  );
}

/* ------------------------------------------------------------------ *
 *  Section Card (Centered Single Column Layout Container)
 * ------------------------------------------------------------------ */
function SectionCard({ title, children, className = "" }) {
  return (
    <div className={`rounded-2xl bg-white p-6 sm:p-8 shadow-xs border border-slate-200 ring-1 ring-slate-200/80 ${className}`}>
      <div className="flex justify-center py-2">
        <div className="w-full max-w-xl space-y-4 text-left">
          {title && (
            <div className="border-b border-slate-200 pb-3">
              <h2 className="text-base font-bold text-slate-900">{title}</h2>
            </div>
          )}
          {children}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 *  MAIN LAB ONBOARDING COMPONENT
 * ------------------------------------------------------------------ */
export default function LabOnboarding({
  readOnly = false,
  centerCode = null,
  draftKey: propDraftKey = null,
  onExit = null,
  onApprove = null,
  onReject = null,
  approving = false,
  rejecting = false,
}) {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const form = useSelector((state) => state.labOnboarding.form);
  const draftKey = useSelector((state) => state.labOnboarding.draftKey);
  const submittedCenterCode = useSelector((state) => state.labOnboarding.submittedCenterCode);
  const status = useSelector((state) => state.labOnboarding.status);
  const errorMsg = useSelector((state) => state.labOnboarding.error);
  const { states: stateOptions, rawStates, loading: stateLoading } = useStateOptions();

  const [cityOptions, setCityOptions] = useState([]);
  const [cityLoading, setCityLoading] = useState(false);

  const stateCode = useMemo(() => {
    return resolveStateCode(form.basic.state, rawStates);
  }, [form.basic.state, rawStates]);

  useEffect(() => {
    if (!stateCode) {
      setCityOptions([]);
      return;
    }
    let active = true;
    setCityLoading(true);
    labOnboardingApi
      .getSimplifyCityList(stateCode)
      .then((res) => {
        if (!active) return;
        const list = res?.data?.data || res?.data || [];
        const cities = Array.isArray(list) ? list : [];
        setCityOptions(cities);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Failed to load cities for state", stateCode, err);
        setCityOptions([]);
      })
      .finally(() => {
        if (active) setCityLoading(false);
      });
    return () => {
      active = false;
    };
  }, [stateCode]);

  // Sync city name when samplifyCityCode is present and city is not set
  useEffect(() => {
    if (cityOptions.length && form.basic.samplifyCityCode && !form.basic.city) {
      const match = cityOptions.find(
        (c) => String(c.city_code).toUpperCase() === String(form.basic.samplifyCityCode).toUpperCase()
      );
      if (match) {
        dispatch(setBasicField({ field: "city", value: match.city_name }));
      }
    }
  }, [cityOptions, form.basic.samplifyCityCode, form.basic.city, dispatch]);

  const [previewDoc, setPreviewDoc] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isResubmitting, setIsResubmitting] = useState(false);
  const [approvalHistory, setApprovalHistory] = useState([]);

  const reduxDetail = useSelector((s) => s.ccOnboarding?.detail?.data);
  useEffect(() => {
    if (reduxDetail) {
      const extracted = extractApprovalHistory(reduxDetail);
      if (extracted.length) {
        setApprovalHistory(extracted);
      }
    }
  }, [reduxDetail]);

  // Resume draft from URL / prop or fetch existing record details if centerCode is present
  useEffect(() => {
    const activeCenterCode = centerCode || searchParams.get("centerCode");
    const activeDraftKey = propDraftKey || searchParams.get("draftKey") || searchParams.get("draft_key");

    if (activeCenterCode) {
      setDetailLoading(true);
      labOnboardingApi
        .getSubmittedLabDetail(activeCenterCode)
        .then((res) => {
          const d = res?.data?.data || res?.data || {};
          const historyFromDetail = extractApprovalHistory(d);
          if (historyFromDetail.length) {
            setApprovalHistory(historyFromDetail);
          }
          const serverForm = d.form || {};
          const tabs = d.tabs || [];
          const tab1 = serverForm.basic || tabs.find((t) => t.stepNo === 1 || t.id === "labInfo")?.data || d.labInfo || d;
          const tab2 = serverForm.contacts || tabs.find((t) => t.stepNo === 2 || t.id === "contacts")?.data || d.contacts || {};
          const tab3 = serverForm.timings ? { timings: serverForm.timings, testCities: serverForm.testCities } : tabs.find((t) => t.stepNo === 3 || t.id === "testTimings")?.data || d.testTimings || {};
          const tab4 = serverForm.basic || tabs.find((t) => t.stepNo === 4 || t.id === "payment")?.data || d.payment || {};
          const tab5 = serverForm.basic || tabs.find((t) => t.stepNo === 5 || t.id === "compliance")?.data || d.compliance || {};
          const tab6 = serverForm.bank || tabs.find((t) => t.stepNo === 6 || t.id === "bank")?.data || d.bank || {};
          const tab7 = tabs.find((t) => t.stepNo === 7 || t.id === "documents")?.data || d.documents || {};

          // Populate basic fields
          dispatch(
            setBasicFields({
              labName: tab1.labName || tab1.name || tab1.ccName || d.name || "",
              mobileNumber: tab1.mobileNumber || tab1.phone || "",
              receptionNumber: tab1.receptionNumber || tab1.landline || "",
              type: tab1.type || (tab1.diagnosticType === 1 ? "pathology" : tab1.diagnosticType === 2 ? "radiology" : ""),
              location: tab1.location || (tab1.latitude && tab1.longitude ? `${tab1.latitude}, ${tab1.longitude}` : ""),
              latitude: tab1.latitude ? String(tab1.latitude) : "",
              longitude: tab1.longitude ? String(tab1.longitude) : "",
              address: tab1.address || "",
              area: tab1.area || "",
              city: tab1.city || tab1.cityName || "",
              samplifyCityCode: tab1.samplifyCityCode || tab1.cityCode || "",
              state: tab1.state || tab1.stateName || "",
              stateCode: tab1.stateCode || "",
              pinCode: tab1.pinCode || tab1.pincode || "",
              circleCode: tab1.circleCode || "",
              circleName: tab1.circleName || "",
              zoneCode: tab1.zoneCode || "",
              zoneName: tab1.zoneName || "",
              gstNumber: tab5.gstNumber || tab1.gstNumber || "",
              nablNumber: tab5.nablNumber || tab1.nablNumber || "",
              panNumber: tab5.panNumber || tab1.panNumber || "",
              paymentPeriod: tab4.paymentPeriod || tab1.paymentPeriod || "monthly",
              paymentReconciliation: tab4.paymentReconciliation || tab1.paymentReconciliation || "30",
              agreementDate: tab5.agreementDate || tab1.agreementDate || "",
              terminationNoticePeriod: tab5.terminationNoticePeriod ? String(tab5.terminationNoticePeriod) : "30",
              isCureBayLabCenter: tab1.isCureBayLabCenter === 1 || tab1.isCureBayLabCenter === "yes" ? "yes" : "no",
              description: tab1.description || "",
            })
          );

          // Populate timings
          const timingSlots = Array.isArray(serverForm.timings)
            ? serverForm.timings
            : Array.isArray(tab3.timings)
            ? tab3.timings
            : [];
          if (timingSlots.length) {
            dispatch(setTimings(timingSlots));
          }

          // Populate contacts
          const contactsList = Array.isArray(serverForm.contacts)
            ? serverForm.contacts
            : Array.isArray(tab2.contacts || tab2)
            ? tab2.contacts || tab2
            : [];
          if (contactsList.length) {
            contactsList.forEach((c, idx) => {
              const contactId = form.contacts[idx]?.id || c.id || `contact-${idx + 1}`;
              if (idx > 0 && !form.contacts.find((x) => x.id === contactId)) {
                dispatch(addContact());
              }
              dispatch(updateContact({ id: contactId, field: "firstName", value: c.firstName || "" }));
              dispatch(updateContact({ id: contactId, field: "lastName", value: c.lastName || "" }));
              dispatch(updateContact({ id: contactId, field: "mobileNumber", value: c.mobileNumber || "" }));
              dispatch(updateContact({ id: contactId, field: "email", value: c.email || "" }));
              dispatch(updateContact({ id: contactId, field: "gender", value: c.gender || "Male" }));
              dispatch(updateContact({ id: contactId, field: "salutation", value: c.salutation || "Mr." }));
              dispatch(updateContact({ id: contactId, field: "panNumber", value: c.panNumber || "" }));
              dispatch(updateContact({ id: contactId, field: "designation", value: c.designation || "" }));
              dispatch(updateContact({ id: contactId, field: "qualification", value: c.qualification || "" }));
              dispatch(updateContact({ id: contactId, field: "dob", value: c.dob || "" }));
              dispatch(updateContact({ id: contactId, field: "source", value: c.source || "" }));
            });
          }

          // Populate bank
          const b = serverForm.bank || tab6.bank || tab6 || {};
          if (b.bankName || b.accountNumber) {
            dispatch(setBankField({ field: "bankName", value: b.bankName || "" }));
            dispatch(setBankField({ field: "accountName", value: b.accountName || b.accountHolderName || "" }));
            dispatch(setBankField({ field: "accountNumber", value: b.accountNumber || "" }));
            dispatch(setBankField({ field: "ifscCode", value: b.ifscCode || "" }));
            dispatch(setBankField({ field: "location", value: b.location || b.branchLocation || "" }));
          }

          if (serverForm.notes || d.notes) {
            dispatch(setNotes(serverForm.notes || d.notes));
          }

          // Map documents (string URLs or attachment objects)
          const wrapDoc = (val, defaultDesc) => {
            if (!val) return null;
            if (typeof val === "string") {
              const name = val.split("/").pop() || "document";
              return { name, url: val, previewUrl: val, description: defaultDesc };
            }
            return {
              attachmentId: val.id || val.attachmentId,
              name: val.name || val.fileName || "document",
              url: val.viewUrl || val.url || val.previewUrl || val.fileUrl || "",
              previewUrl: val.viewUrl || val.url || val.previewUrl || "",
              description: val.description || defaultDesc,
            };
          };

          if (tab1.agreementCopy) dispatch(setDocument({ docKey: "agreementCopy", docData: wrapDoc(tab1.agreementCopy, "Agreement Copy") }));
          if (tab1.bankProof) dispatch(setDocument({ docKey: "bankProof", docData: wrapDoc(tab1.bankProof, "Cancelled Cheque") }));
          if (tab1.labLogo) dispatch(setDocument({ docKey: "labLogo", docData: wrapDoc(tab1.labLogo, "Lab Logo") }));
          if (tab1.kycDocument) dispatch(setDocument({ docKey: "kycDocument", docData: wrapDoc(tab1.kycDocument, "KYC Document") }));
          if (tab1.nablDocument) dispatch(setDocument({ docKey: "nablDocument", docData: wrapDoc(tab1.nablDocument, "NABL Certificate") }));
          if (tab1.gstDocument) dispatch(setDocument({ docKey: "gstDocument", docData: wrapDoc(tab1.gstDocument, "GST Certificate") }));

          const docs = Array.isArray(d.attachments) ? d.attachments : Array.isArray(tab7.documents) ? tab7.documents : [];
          docs.forEach((att) => {
            const desc = att.description || "";
            const fileData = wrapDoc(att, desc);
            if (desc.includes("Agreement Copy")) dispatch(setDocument({ docKey: "agreementCopy", docData: fileData }));
            else if (desc.includes("Cancelled Cheque")) dispatch(setDocument({ docKey: "bankProof", docData: fileData }));
            else if (desc.includes("Lab Logo")) dispatch(setDocument({ docKey: "labLogo", docData: fileData }));
            else if (desc.includes("KYC Document") || desc.includes("KYC Pan") || desc.includes("KYC PAN")) dispatch(setDocument({ docKey: "kycDocument", docData: fileData }));
            else if (desc.includes("NABL Certificate")) dispatch(setDocument({ docKey: "nablDocument", docData: fileData }));
            else if (desc.includes("GST Certificate")) dispatch(setDocument({ docKey: "gstDocument", docData: fileData }));
          });
        })
        .catch((err) => {
          console.warn("Could not fetch centre detail:", err);
        })
        .finally(() => {
          setDetailLoading(false);
        });
    } else if (activeDraftKey) {
      setDetailLoading(true);
      dispatch(resumeLabDraftServer(activeDraftKey))
        .unwrap()
        .then(() => toast.info("Loaded lab draft details."))
        .catch((err) => toast.error(err || "Failed to load saved draft"))
        .finally(() => {
          setDetailLoading(false);
        });
    }
  }, [centerCode, propDraftKey, searchParams, dispatch]);

  // Auto-fill address, city, state, pin code from location picker
  const handleLocationConfirmed = ({ latLngStr, lat, lng, addressData }) => {
    dispatch(setBasicField({ field: "location", value: latLngStr }));
    dispatch(setBasicField({ field: "latitude", value: String(lat) }));
    dispatch(setBasicField({ field: "longitude", value: String(lng) }));

    if (addressData) {
      if (addressData.address && !form.basic.address) {
        dispatch(setBasicField({ field: "address", value: addressData.address }));
      }
      if (addressData.area && !form.basic.area) {
        dispatch(setBasicField({ field: "area", value: addressData.area }));
      }
      if (addressData.city && !form.basic.city) {
        dispatch(setBasicField({ field: "city", value: addressData.city }));
      }
      if (addressData.state && !form.basic.state) {
        dispatch(setBasicField({ field: "state", value: addressData.state }));
      }
      if (addressData.pinCode && !form.basic.pinCode) {
        dispatch(setBasicField({ field: "pinCode", value: addressData.pinCode }));
      }
    }
    toast.success("Location pinpointed & address auto-filled!");
  };

  // Timings Slot logic matching CC Onboarding
  const timings = form.timings || [];
  const getAssignedDays = (excludeId = null) => {
    const assigned = new Set();
    timings.forEach((t) => {
      if (excludeId && t.id === excludeId) return;
      const days = Array.isArray(t.days) ? t.days : [];
      days.forEach((d) => assigned.add(d));
    });
    return assigned;
  };

  const allAssignedDays = getAssignedDays();
  const allDaysCovered = DAYS.every((d) => allAssignedDays.has(d));
  const hasEmptySlot = timings.some((t) => (t.days || []).length === 0);
  const canAddSlot = !readOnly && !allDaysCovered && !hasEmptySlot;

  const handleToggleDay = (timingId, day) => {
    if (readOnly) return;
    const nextTimings = timings.map((t) => {
      const currentDays = Array.isArray(t.days) ? t.days : [];
      if (t.id === timingId) {
        const nextDays = currentDays.includes(day)
          ? currentDays.filter((d) => d !== day)
          : [...currentDays, day];
        return { ...t, days: nextDays };
      } else {
        return { ...t, days: currentDays.filter((d) => d !== day) };
      }
    });
    dispatch(setTimings(nextTimings));
  };

  const handleSetPreset = (timingId, presetType) => {
    if (readOnly) return;
    let targetDays = [];
    if (presetType === "ALL") targetDays = [...DAYS];
    else if (presetType === "WEEKDAYS") targetDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
    else if (presetType === "WEEKEND") targetDays = ["Saturday", "Sunday"];
    else if (presetType === "CLEAR") targetDays = [];

    const nextTimings = timings.map((t) => {
      const currentDays = Array.isArray(t.days) ? t.days : [];
      if (t.id === timingId) {
        return { ...t, days: targetDays };
      } else {
        return { ...t, days: currentDays.filter((d) => !targetDays.includes(d)) };
      }
    });
    dispatch(setTimings(nextTimings));
  };

  const handleSaveDraft = async () => {
    dispatch(saveDraft());
    try {
      const res = await dispatch(saveLabDraftServer({ remarks: form.notes || "Draft saved from web screen" })).unwrap();
      toast.success(
        <div>
          <p className="font-semibold">Draft Saved Successfully!</p>
          <p className="text-xs text-slate-200">Key: {res.draftKey}</p>
        </div>
      );
    } catch (err) {
      toast.error(err || "Failed to save draft to server");
    }
  };

  const handleUpdateLab = async () => {
    const activeCenterCode = centerCode || searchParams.get("centerCode");
    if (!activeCenterCode) {
      toast.error("Center Code is missing for update");
      return;
    }

    const errors = validateForm();
    if (errors.length > 0) {
      toast.error(
        <div>
          <p className="font-semibold">Please resolve the following:</p>
          <ul className="mt-1 list-disc pl-4 text-xs">
            {errors.slice(0, 4).map((err, i) => (
              <li key={i}>{err}</li>
            ))}
            {errors.length > 4 && <li>...and {errors.length - 4} more</li>}
          </ul>
        </div>
      );
      return;
    }

    setIsUpdating(true);
    try {
      const activeDraftKey = propDraftKey || draftKey || searchParams.get("draftKey") || searchParams.get("draft_key");
      const remarks = form.notes || "Updated lab details";

      // 1. Send complete update payload body to server
      await labOnboardingApi.updateSubmittedLab({
        centerCode: activeCenterCode,
        draftKey: activeDraftKey || undefined,
        form,
        remarks,
      });

      // 2. Sync draft if present
      if (activeDraftKey) {
        try {
          await labOnboardingApi.updateSavedLabDraft({
            draftKey: activeDraftKey,
            form,
            remarks,
          });
        } catch {
          // ignore draft sync errors
        }
      }

      toast.success("Lab details updated successfully!");
      if (onExit) onExit();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || err || "Failed to update lab details");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleResubmitLab = async () => {
    const activeCenterCode = centerCode || searchParams.get("centerCode");
    const activeDraftKey = propDraftKey || draftKey || searchParams.get("draftKey") || searchParams.get("draft_key");

    const errors = validateForm();
    if (errors.length > 0) {
      toast.error(
        <div>
          <p className="font-semibold">Please resolve the following:</p>
          <ul className="mt-1 list-disc pl-4 text-xs">
            {errors.slice(0, 4).map((err, i) => (
              <li key={i}>{err}</li>
            ))}
            {errors.length > 4 && <li>...and {errors.length - 4} more</li>}
          </ul>
        </div>
      );
      return;
    }

    setIsResubmitting(true);
    try {
      if (activeCenterCode) {
        // 1. Save any field modifications with full request body first
        try {
          await labOnboardingApi.updateSubmittedLab({
            centerCode: activeCenterCode,
            draftKey: activeDraftKey || undefined,
            form,
            remarks: form.notes || "Updated before resubmit",
          });
        } catch {
          // continue with resubmit
        }
        // 2. Resubmit the facility
        await dispatch(
          resubmitCentre({
            centerCode: activeCenterCode,
            draftKey: activeDraftKey || undefined,
            remarks: form.notes || "Resubmitted after corrections",
          })
        ).unwrap();
        toast.success("Lab resubmitted successfully for review!");
      } else if (activeDraftKey) {
        await dispatch(
          submitLabDraftServer({ remarks: form.notes || "Resubmitted from Lab Onboarding" })
        ).unwrap();
        toast.success("Lab resubmitted successfully for review!");
      }
      if (onExit) onExit();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || err || "Failed to resubmit lab");
    } finally {
      setIsResubmitting(false);
    }
  };

  const handleResetForm = () => {
    if (window.confirm("Are you sure you want to clear all entered lab details?")) {
      dispatch(resetForm());
      toast.info("Form reset successfully.");
    }
  };

  const validateForm = () => {
    const errors = [];
    const b = form.basic;

    // 1. Basic & Legal Information
    if (!b.labName?.trim()) errors.push("Lab Name is required");

    if (b.mobileNumber?.trim()) {
      if (!/^\d{10}$/.test(b.mobileNumber.trim())) {
        errors.push("Lab Mobile Number must be exactly 10 digits");
      }
    }

    if (!b.type?.trim()) errors.push("Select Lab Type (Pathology or Radiology)");
    if (!b.address?.trim()) errors.push("Lab Address is required");
    if (!b.state?.trim()) errors.push("State is required");
    if (!b.city?.trim()) errors.push("City is required");

    if (!b.pinCode?.trim()) {
      errors.push("Pin Code is required");
    } else if (!/^\d{6}$/.test(b.pinCode.trim())) {
      errors.push("Pin Code must be exactly 6 digits");
    }

    // Company PAN validation (Mandatory)
    if (!b.panNumber?.trim()) {
      errors.push("Company PAN is required");
    } else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(b.panNumber.trim().toUpperCase())) {
      errors.push("Company PAN must be a valid 10-character alphanumeric PAN (e.g. ABCDE1234F)");
    }

    if (!b.paymentPeriod?.trim()) errors.push("Payment Period is required");
    if (!b.paymentReconciliation?.trim()) errors.push("Payment Reconciliation is required");
    if (!b.agreementDate?.trim()) errors.push("Agreement Date is required");
    if (!b.terminationNoticePeriod?.trim()) errors.push("Termination Notice Period is required");

    // 2. Documents Validation (KYC Pan Upload & Bank Proof are mandatory)
    const hasDoc = (doc) => Boolean(doc && (doc.url || doc.previewUrl || doc.file || doc.attachmentId));
    if (!hasDoc(b.kycDocument)) {
      errors.push("KYC Pan Upload is required");
    }
    if (!hasDoc(b.bankProof)) {
      errors.push("Cancelled Cheque / Bank Details document is required");
    }

    // 3. Validate Contacts (Mobile 10 digits only, Email format, No PAN)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!form.contacts || form.contacts.length === 0) {
      errors.push("At least one Contact Person is required");
    } else {
      form.contacts.forEach((c, idx) => {
        const prefix = `Contact #${idx + 1}`;
        if (!c.firstName?.trim()) errors.push(`${prefix}: First Name is required`);
        if (!c.lastName?.trim()) errors.push(`${prefix}: Last Name is required`);
        if (!c.gender?.trim()) errors.push(`${prefix}: Gender is required`);

        if (!c.mobileNumber?.trim()) {
          errors.push(`${prefix}: Mobile Number is required`);
        } else if (!/^\d{10}$/.test(c.mobileNumber.trim())) {
          errors.push(`${prefix}: Mobile Number must be exactly 10 digits`);
        }

        if (!c.email?.trim()) {
          errors.push(`${prefix}: Email ID is required`);
        } else if (!emailRegex.test(c.email.trim())) {
          errors.push(`${prefix}: Enter a valid Email address`);
        }
      });
    }

    // 4. Validate Timings
    if (timings.length === 0) {
      errors.push("At least one lab timing slot is required");
    } else {
      timings.forEach((t, idx) => {
        if ((t.days || []).length === 0) errors.push(`Timing Slot #${idx + 1}: Select at least one day`);
        if (!t.opensAt) errors.push(`Timing Slot #${idx + 1}: Open time is required`);
        if (!t.closesAt) errors.push(`Timing Slot #${idx + 1}: Close time is required`);
      });
    }

    // 5. Bank Details (Mandatory)
    const bank = form.bank || {};
    if (!bank.bankName?.trim()) errors.push("Bank Name is required");
    if (!bank.accountName?.trim()) errors.push("Account Holder Name is required");
    if (!bank.accountNumber?.trim()) errors.push("Account Number is required");
    if (!bank.ifscCode?.trim()) {
      errors.push("IFSC Code is required");
    } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(bank.ifscCode.trim().toUpperCase())) {
      errors.push("Invalid IFSC Code format (e.g. SBIN0001234)");
    }
    if (!bank.location?.trim()) errors.push("Branch Location is required");

    return errors;
  };

  const handleSubmitReview = async () => {
    const errors = validateForm();
    if (errors.length > 0) {
      toast.error(
        <div>
          <p className="font-semibold">Please resolve the following:</p>
          <ul className="mt-1 list-disc pl-4 text-xs">
            {errors.slice(0, 4).map((err, i) => (
              <li key={i}>{err}</li>
            ))}
            {errors.length > 4 && <li>...and {errors.length - 4} more</li>}
          </ul>
        </div>
      );
      return;
    }

    try {
      const res = await dispatch(
        submitLabDraftServer({ remarks: form.notes || "Submitted from the new Lab onboarding screen" })
      ).unwrap();
      toast.success(`Lab submitted successfully! Facility Code: ${res.centerCode}`);
    } catch (err) {
      toast.error(err || "Failed to submit lab onboarding");
    }
  };

  if (detailLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (status === "submitted" && !centerCode) {
    return (
      <div className="flex min-h-[550px] w-full flex-col items-center justify-center rounded-2xl bg-white px-6 py-20 text-center shadow-xs border border-slate-200 ring-1 ring-slate-100">
        <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-emerald-100 ring-8 ring-emerald-50">
          <Check className="h-10 w-10 text-emerald-600" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900">Lab Details Submitted for Review</h2>
        {submittedCenterCode && (
          <div className="mt-3 rounded-lg bg-indigo-50 px-4 py-2 border border-indigo-200 text-indigo-900 text-sm font-semibold inline-block">
            Facility Code: {submittedCenterCode}
          </div>
        )}
        <p className="mt-3 max-w-md text-sm text-slate-500">
          The operations and compliance team will verify the documents and approve within 24–48 hours.
        </p>
        <div className="mt-8 flex items-center gap-3">
          {onExit && (
            <button
              type="button"
              onClick={onExit}
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition"
            >
              <ChevronLeft className="h-4 w-4" /> Back to List
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              dispatch(resetForm());
            }}
            className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700"
          >
            <Plus className="h-4 w-4" /> Onboard Another Lab
          </button>
        </div>
      </div>
    );
  }

  const isReviewMode = Boolean(onApprove && onReject);
  const isEditExistingMode = Boolean(centerCode && !readOnly);

  return (
    <div className="min-h-screen bg-slate-50/70 pb-28 pt-2">
      <div className="mx-auto max-w-5xl px-4 py-4 sm:px-8 space-y-8">
        {/* Basic & Legal Information */}
        <SectionCard title="Basic & Legal Information">
          <Field label="Lab Name" required>
            <TextInput
              placeholder="e.g. Sunrise Diagnostics"
              value={form.basic.labName}
              disabled={readOnly}
              onChange={(e) => dispatch(setBasicField({ field: "labName", value: e.target.value }))}
            />
          </Field>

          <Field label="Mobile Number">
            <TextInput
              placeholder="10-digit mobile number (Optional)"
              maxLength={10}
              value={form.basic.mobileNumber}
              disabled={readOnly}
              onChange={(e) =>
                dispatch(
                  setBasicField({
                    field: "mobileNumber",
                    value: e.target.value.replace(/\D/g, "").slice(0, 10),
                  })
                )
              }
            />
          </Field>

          <Field label="Reception Number">
            <TextInput
              placeholder="Enter reception phone"
              maxLength={12}
              value={form.basic.receptionNumber}
              disabled={readOnly}
              onChange={(e) =>
                dispatch(
                  setBasicField({
                    field: "receptionNumber",
                    value: e.target.value.replace(/\D/g, "").slice(0, 12),
                  })
                )
              }
            />
          </Field>

          <Field label="Lab Type" required>
            <SearchableSelect
              placeholder="Select Type"
              searchPlaceholder="Search type…"
              options={LAB_TYPE_OPTIONS}
              value={form.basic.type}
              disabled={readOnly}
              getLabel={(o) => (typeof o === "string" ? o : o.label || o.value)}
              getKey={(o) => (typeof o === "string" ? o : o.value)}
              onChange={(val) => dispatch(setBasicField({ field: "type", value: val }))}
            />
          </Field>

          <Field label="Location" required>
            <LocationPickerField
              value={form.basic.location}
              latitude={form.basic.latitude}
              longitude={form.basic.longitude}
              disabled={readOnly}
              onConfirmLocation={handleLocationConfirmed}
            />
          </Field>

          <Field label="Address" required>
            <TextArea
              rows={3}
              placeholder="Full address"
              value={form.basic.address}
              disabled={readOnly}
              onChange={(e) => dispatch(setBasicField({ field: "address", value: e.target.value }))}
            />
          </Field>

          <Field label="Area / Locality">
            <TextInput
              placeholder="Area or landmark"
              value={form.basic.area}
              disabled={readOnly}
              onChange={(e) => dispatch(setBasicField({ field: "area", value: e.target.value }))}
            />
          </Field>

          <Field label="State" required>
            <SearchableSelect
              placeholder={stateLoading ? "Loading…" : "Select State"}
              searchPlaceholder="Search state…"
              options={stateOptions}
              value={form.basic.state}
              disabled={readOnly}
              getLabel={(s) => (typeof s === "string" ? s : s?.stateName || s?.name || s?.label || "")}
              getKey={(s) => (typeof s === "string" ? s : s?.stateName || s?.name || s?.stateCode || s?.code || "")}
              onChange={(val, opt) => {
                const stateName = typeof opt === "object" ? opt?.stateName || opt?.name || val : val;
                const sCode = typeof opt === "object" ? opt?.stateCode || opt?.code || "" : resolveStateCode(val, rawStates);
                dispatch(setBasicField({ field: "state", value: stateName }));
                dispatch(setBasicField({ field: "stateCode", value: sCode }));
                if (stateName !== form.basic.state) {
                  dispatch(setBasicField({ field: "city", value: "" }));
                  dispatch(setBasicField({ field: "samplifyCityCode", value: "" }));
                }
              }}
            />
          </Field>

          <Field label="Samplify City">
            <SearchableSelect
              placeholder={
                !form.basic.state
                  ? "Select State first"
                  : cityLoading
                  ? "Loading cities…"
                  : "Select Samplify City"
              }
              searchPlaceholder="Search samplify city…"
              options={cityOptions}
              value={form.basic.samplifyCityCode}
              disabled={readOnly || !form.basic.state}
              getLabel={(c) => (typeof c === "string" ? c : c?.city_name || c?.cityName || c?.name || c?.city_code || "")}
              getKey={(c) => (typeof c === "string" ? c : c?.city_code || c?.cityCode || c?.code || c?.id || c?.city_name || "")}
              onChange={(val, opt) => {
                const selectedCityObj = cityOptions.find(
                  (c) =>
                    c.city_code === val ||
                    c.cityCode === val ||
                    String(c.id) === String(val) ||
                    c.city_name === val
                );
                const cityName =
                  selectedCityObj?.city_name ||
                  selectedCityObj?.name ||
                  (typeof opt === "object" ? opt?.city_name || opt?.name || opt?.label : val);
                const cityCode = selectedCityObj?.city_code || val;
                dispatch(setBasicField({ field: "samplifyCityCode", value: cityCode || "" }));
                if (cityName) {
                  dispatch(setBasicField({ field: "city", value: cityName }));
                }
              }}
            />
          </Field>

          <Field label="City" required>
            <TextInput
              placeholder="Enter city name (e.g. Bhubaneswar)"
              value={form.basic.city}
              disabled={readOnly}
              onChange={(e) => dispatch(setBasicField({ field: "city", value: e.target.value }))}
            />
          </Field>

          <Field label="Pin Code" required>
            <TextInput
              placeholder="751001"
              maxLength={6}
              value={form.basic.pinCode}
              disabled={readOnly}
              onChange={(e) =>
                dispatch(
                  setBasicField({
                    field: "pinCode",
                    value: e.target.value.replace(/\D/g, "").slice(0, 6),
                  })
                )
              }
            />
          </Field>

          <Field label="Company PAN" required>
            <TextInput
              placeholder="e.g. ABCDE1234F"
              maxLength={10}
              value={form.basic.panNumber}
              disabled={readOnly}
              onChange={(e) =>
                dispatch(
                  setBasicField({
                    field: "panNumber",
                    value: e.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 10),
                  })
                )
              }
            />
          </Field>

          <Field label="GST Registration Number">
            <TextInput
              placeholder="Enter GST number"
              value={form.basic.gstNumber}
              disabled={readOnly}
              onChange={(e) => dispatch(setBasicField({ field: "gstNumber", value: e.target.value?.toUpperCase() }))}
            />
          </Field>

          <Field label="NABL Number">
            <TextInput
              placeholder="Enter NABL accreditation number"
              value={form.basic.nablNumber}
              disabled={readOnly}
              onChange={(e) => dispatch(setBasicField({ field: "nablNumber", value: e.target.value }))}
            />
          </Field>

          <Field label="Payment Period" required>
            <SearchableSelect
              placeholder="Select Period"
              options={PAYMENT_PERIOD_OPTIONS}
              value={form.basic.paymentPeriod}
              disabled={readOnly}
              getLabel={(o) => (typeof o === "string" ? o : o.label || o.value)}
              getKey={(o) => (typeof o === "string" ? o : o.value)}
              onChange={(val) => dispatch(setBasicField({ field: "paymentPeriod", value: val }))}
            />
          </Field>

          <Field label="Payment Reconciliation" required>
            <SearchableSelect
              placeholder="Select Reconciliation Days"
              options={PAYMENT_RECONCILIATION_OPTIONS}
              value={form.basic.paymentReconciliation}
              disabled={readOnly}
              getLabel={(o) => (typeof o === "string" ? o : o.label || o.value)}
              getKey={(o) => (typeof o === "string" ? o : o.value)}
              onChange={(val) => dispatch(setBasicField({ field: "paymentReconciliation", value: val }))}
            />
          </Field>

          <Field label="Agreement Date" required>
            <DateInput
              value={form.basic.agreementDate}
              disabled={readOnly}
              onChange={(val) => dispatch(setBasicField({ field: "agreementDate", value: val }))}
            />
          </Field>

          <Field label="Termination Notice Period (Days)" required>
            <TextInput
              type="number"
              placeholder="e.g. 30"
              value={form.basic.terminationNoticePeriod}
              disabled={readOnly}
              onChange={(e) => dispatch(setBasicField({ field: "terminationNoticePeriod", value: e.target.value }))}
            />
          </Field>

          <Field label="Is it Curebay Lab Center">
            <SearchableSelect
              placeholder="Select"
              options={CUREBAY_LAB_OPTIONS}
              value={form.basic.isCureBayLabCenter}
              disabled={readOnly}
              getLabel={(o) => (typeof o === "string" ? o : o.label || o.value)}
              getKey={(o) => (typeof o === "string" ? o : o.value)}
              onChange={(val) => dispatch(setBasicField({ field: "isCureBayLabCenter", value: val }))}
            />
          </Field>

          <Field label="Description / Notes">
            <TextInput
              placeholder="Enter description or special notes"
              value={form.basic.description}
              disabled={readOnly}
              onChange={(e) => dispatch(setBasicField({ field: "description", value: e.target.value }))}
            />
          </Field>

          {/* Legal Documents in Single Column */}
          <div className="pt-4 border-t border-slate-200 space-y-3.5">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">
              Lab &amp; Legal Documents
            </h3>

            <Field label="Agreement Copy">
              <FileInput
                file={form.basic.agreementCopy}
                hint="Upload Agreement Copy"
                readOnly={readOnly}
                onChange={(data) => dispatch(setDocument({ docKey: "agreementCopy", docData: data }))}
                onDelete={() => dispatch(deleteDocument({ docKey: "agreementCopy" }))}
                onPreview={(doc) => setPreviewDoc(doc)}
              />
            </Field>

            <Field label="Cancelled Cheque / Bank Details" required>
              <FileInput
                file={form.basic.bankProof}
                hint="Upload Cancelled Cheque"
                readOnly={readOnly}
                onChange={(data) => dispatch(setDocument({ docKey: "bankProof", docData: data }))}
                onDelete={() => dispatch(deleteDocument({ docKey: "bankProof" }))}
                onPreview={(doc) => setPreviewDoc(doc)}
              />
            </Field>

            <Field label="Lab Logo">
              <FileInput
                file={form.basic.labLogo}
                hint="Upload Lab Logo"
                readOnly={readOnly}
                onChange={(data) => dispatch(setDocument({ docKey: "labLogo", docData: data }))}
                onDelete={() => dispatch(deleteDocument({ docKey: "labLogo" }))}
                onPreview={(doc) => setPreviewDoc(doc)}
              />
            </Field>

            <Field label="KYC Pan Upload" required>
              <FileInput
                file={form.basic.kycDocument}
                hint="Upload KYC PAN Document"
                readOnly={readOnly}
                onChange={(data) => dispatch(setDocument({ docKey: "kycDocument", docData: data }))}
                onDelete={() => dispatch(deleteDocument({ docKey: "kycDocument" }))}
                onPreview={(doc) => setPreviewDoc(doc)}
              />
            </Field>

            <Field label="NABL Certificate">
              <FileInput
                file={form.basic.nablDocument}
                hint="Upload NABL Certificate"
                readOnly={readOnly}
                onChange={(data) => dispatch(setDocument({ docKey: "nablDocument", docData: data }))}
                onDelete={() => dispatch(deleteDocument({ docKey: "nablDocument" }))}
                onPreview={(doc) => setPreviewDoc(doc)}
              />
            </Field>

            <Field label="GST Certificate">
              <FileInput
                file={form.basic.gstDocument}
                hint="Upload GST Certificate"
                readOnly={readOnly}
                onChange={(data) => dispatch(setDocument({ docKey: "gstDocument", docData: data }))}
                onDelete={() => dispatch(deleteDocument({ docKey: "gstDocument" }))}
                onPreview={(doc) => setPreviewDoc(doc)}
              />
            </Field>
          </div>
        </SectionCard>

        {/* Lab Timings */}
        <SectionCard title="Lab Timings">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-600">
                {allAssignedDays.size} of 7 days configured
              </span>
            </div>

            <div className="space-y-4">
              {timings.map((t, idx) => {
                const currentDays = Array.isArray(t.days) ? t.days : [];

                return (
                  <div key={t.id} className="rounded-xl border border-slate-300 bg-slate-50/50 p-4 space-y-3.5">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
                        Timing Slot #{idx + 1}
                      </span>
                      {!readOnly && timings.length > 1 && (
                        <button
                          type="button"
                          onClick={() => dispatch(removeTimingSlot(t.id))}
                          className="flex items-center gap-1 text-xs font-semibold text-rose-500 hover:text-rose-600"
                        >
                          <Trash2 className="h-3.5 w-3.5" /> Remove Slot
                        </button>
                      )}
                    </div>

                    <div>
                      <div className="mb-2 flex items-center justify-between">
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                          Select Operating Days <span className="text-rose-500">*</span>
                        </label>
                        {!readOnly && (
                          <div className="flex items-center gap-1.5 text-xs text-indigo-600 font-medium">
                            <button
                              type="button"
                              onClick={() => handleSetPreset(t.id, "WEEKDAYS")}
                              className="hover:underline"
                            >
                              Mon–Fri
                            </button>
                            <span>•</span>
                            <button
                              type="button"
                              onClick={() => handleSetPreset(t.id, "WEEKEND")}
                              className="hover:underline"
                            >
                              Sat–Sun
                            </button>
                            <span>•</span>
                            <button
                              type="button"
                              onClick={() => handleSetPreset(t.id, "ALL")}
                              className="hover:underline"
                            >
                              All Days
                            </button>
                            <span>•</span>
                            <button
                              type="button"
                              onClick={() => handleSetPreset(t.id, "CLEAR")}
                              className="text-slate-500 hover:text-slate-700"
                            >
                              Clear
                            </button>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {DAYS.map((day) => {
                          const isSelected = currentDays.includes(day);
                          return (
                            <button
                              key={day}
                              type="button"
                              disabled={readOnly}
                              onClick={() => handleToggleDay(t.id, day)}
                              className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                                isSelected
                                  ? "bg-indigo-600 text-white shadow-xs"
                                  : "border border-slate-300 bg-white text-slate-700 hover:border-indigo-400 hover:bg-indigo-50/50 disabled:bg-slate-50 disabled:text-slate-400"
                              }`}
                            >
                              {DAY_SHORT[day]}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <Field label="Opens At" required>
                        <TextInput
                          type="time"
                          value={t.opensAt}
                          disabled={readOnly}
                          onChange={(e) =>
                            dispatch(
                              updateTimingSlot({
                                id: t.id,
                                field: "opensAt",
                                value: e.target.value,
                              })
                            )
                          }
                        />
                      </Field>
                      <Field label="Closes At" required>
                        <TextInput
                          type="time"
                          value={t.closesAt}
                          disabled={readOnly}
                          onChange={(e) =>
                            dispatch(
                              updateTimingSlot({
                                id: t.id,
                                field: "closesAt",
                                value: e.target.value,
                              })
                            )
                          }
                        />
                      </Field>
                    </div>
                  </div>
                );
              })}
            </div>

            {!readOnly && (
              <button
                type="button"
                onClick={() => dispatch(addTimingSlot())}
                disabled={!canAddSlot}
                className={`inline-flex items-center gap-2 rounded-xl border border-dashed px-4 py-2.5 text-xs font-semibold transition w-full justify-center ${
                  !canAddSlot
                    ? "cursor-not-allowed border-slate-300 bg-slate-50 text-slate-400"
                    : "border-indigo-400 text-indigo-600 hover:bg-indigo-50"
                }`}
              >
                <Plus className="h-4 w-4" /> Add Another Timing Slot{" "}
                {allDaysCovered ? "(All 7 days configured)" : hasEmptySlot ? "(Select days above first)" : ""}
              </button>
            )}
          </div>
        </SectionCard>

        {/* Contact Person(s) */}
        <SectionCard title="Contact Person(s)">
          <div className="space-y-6">
            {form.contacts.map((c, idx) => (
              <div key={c.id} className="rounded-xl border border-slate-300 bg-slate-50/40 p-4 sm:p-5 space-y-3.5">
                <div className="flex items-center justify-between border-b border-slate-200 pb-3">
                  <span className="rounded-md bg-indigo-50 px-2.5 py-1 text-xs font-bold text-indigo-600">
                    Contact Person #{idx + 1}
                  </span>
                  {!readOnly && form.contacts.length > 1 && (
                    <button
                      type="button"
                      onClick={() => dispatch(removeContact(c.id))}
                      className="flex items-center gap-1 text-xs font-medium text-rose-500 hover:text-rose-600"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Remove
                    </button>
                  )}
                </div>

                <div className="space-y-3.5">
                  <Field label="Salutation">
                    <SearchableSelect
                      placeholder="Select"
                      options={SALUTATION_OPTIONS}
                      value={c.salutation}
                      disabled={readOnly}
                      onChange={(val) => dispatch(updateContact({ id: c.id, field: "salutation", value: val }))}
                    />
                  </Field>

                  <Field label="First Name" required>
                    <TextInput
                      placeholder="First name"
                      value={c.firstName}
                      disabled={readOnly}
                      onChange={(e) => dispatch(updateContact({ id: c.id, field: "firstName", value: e.target.value }))}
                    />
                  </Field>

                  <Field label="Last Name" required>
                    <TextInput
                      placeholder="Last name"
                      value={c.lastName}
                      disabled={readOnly}
                      onChange={(e) => dispatch(updateContact({ id: c.id, field: "lastName", value: e.target.value }))}
                    />
                  </Field>

                  <Field label="Gender" required>
                    <SearchableSelect
                      placeholder="Select Gender"
                      options={GENDER_OPTIONS}
                      value={c.gender}
                      disabled={readOnly}
                      onChange={(val) => dispatch(updateContact({ id: c.id, field: "gender", value: val }))}
                    />
                  </Field>

                  <Field label="Mobile Number" required>
                    <TextInput
                      placeholder="10-digit mobile"
                      maxLength={10}
                      value={c.mobileNumber}
                      disabled={readOnly}
                      onChange={(e) =>
                        dispatch(
                          updateContact({
                            id: c.id,
                            field: "mobileNumber",
                            value: e.target.value.replace(/\D/g, "").slice(0, 10),
                          })
                        )
                      }
                    />
                  </Field>

                  <Field label="Email ID" required>
                    <TextInput
                      type="email"
                      placeholder="contact@lab.com"
                      value={c.email}
                      disabled={readOnly}
                      onChange={(e) => dispatch(updateContact({ id: c.id, field: "email", value: e.target.value }))}
                    />
                  </Field>

                  <Field label="Date of Birth">
                    <DateInput
                      value={c.dob}
                      disabled={readOnly}
                      maxDate={new Date().toISOString().split("T")[0]}
                      onChange={(val) => dispatch(updateContact({ id: c.id, field: "dob", value: val }))}
                    />
                  </Field>

                  <Field label="Designation">
                    <TextInput
                      placeholder="e.g. Lab Director (Optional)"
                      value={c.designation}
                      disabled={readOnly}
                      onChange={(e) => dispatch(updateContact({ id: c.id, field: "designation", value: e.target.value }))}
                    />
                  </Field>

                  <Field label="Qualification">
                    <SearchableSelect
                      placeholder="Select Qualification"
                      options={QUALIFICATION_OPTIONS}
                      value={c.qualification}
                      disabled={readOnly}
                      onChange={(val) => dispatch(updateContact({ id: c.id, field: "qualification", value: val }))}
                    />
                  </Field>

                  <Field label="Source">
                    <SearchableSelect
                      placeholder="Select Source"
                      options={SOURCE_OPTIONS}
                      value={c.source}
                      disabled={readOnly}
                      onChange={(val) => dispatch(updateContact({ id: c.id, field: "source", value: val }))}
                    />
                  </Field>
                </div>
              </div>
            ))}

            {!readOnly && (
              <button
                type="button"
                onClick={() => dispatch(addContact())}
                className="inline-flex items-center gap-1.5 rounded-xl border border-dashed border-indigo-400 px-4 py-2.5 text-xs font-semibold text-indigo-600 transition hover:bg-indigo-50 w-full justify-center"
              >
                <Plus className="h-4 w-4" /> Add Another Contact Person
              </button>
            )}
          </div>
        </SectionCard>

        {/* Test Details */}
        <SectionCard title="Test Details">
          <div className="space-y-4">
            {form.testCities.map((t) => (
              <div key={t.id} className="rounded-xl border border-slate-300 bg-slate-50/50 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-700">City Menu</span>
                  {!readOnly && form.testCities.length > 1 && (
                    <button
                      type="button"
                      onClick={() => dispatch(removeTestCity(t.id))}
                      className="text-xs font-medium text-rose-500 hover:text-rose-600"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>

                <Field label="City Name">
                  <TextInput
                    placeholder="Enter city name (Optional)"
                    value={t.city}
                    disabled={readOnly}
                    onChange={(e) => dispatch(updateTestCity({ id: t.id, field: "city", value: e.target.value }))}
                  />
                </Field>

                <Field label="Test Menu / Price List Files">
                  {readOnly ? (
                    <div className="text-xs text-slate-500 italic bg-white p-2.5 rounded-lg border border-slate-200">
                      {t.files?.length ? (
                        <div className="space-y-1.5 not-italic">
                          {t.files.map((f, fIdx) => {
                            const fUrl = f.previewUrl || f.url || f.fileUrl;
                            const fName = f.name || f.fileName || `Test menu file ${fIdx + 1}`;
                            return (
                              <div key={f.id || fIdx} className="flex items-center justify-between text-xs text-slate-700">
                                <span className="truncate max-w-[240px]">{fName}</span>
                                {fUrl && (
                                  <button
                                    type="button"
                                    onClick={() => setPreviewDoc({ name: fName, url: fUrl, previewUrl: fUrl })}
                                    className="text-indigo-600 font-medium hover:underline cursor-pointer ml-2"
                                  >
                                    View
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : "No test menu files"}
                    </div>
                  ) : (
                    <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-400 bg-white px-3 py-2.5 text-xs font-medium text-slate-700 transition hover:border-indigo-400 hover:bg-indigo-50/40">
                      <Upload className="h-4 w-4 shrink-0 text-indigo-600" />
                      <span className="truncate">
                        {t.files?.length ? `${t.files.length} file(s) selected` : "Upload test menu files"}
                      </span>
                      <input
                        type="file"
                        multiple
                        className="hidden"
                        onChange={(e) =>
                          dispatch(
                            updateTestCity({
                              id: t.id,
                              field: "files",
                              value: Array.from(e.target.files || []).map((f) => ({
                                file: f,
                                name: f.name,
                                size: f.size,
                                type: f.type,
                              })),
                            })
                          )
                        }
                      />
                    </label>
                  )}
                </Field>
              </div>
            ))}

            {!readOnly && (
              <button
                type="button"
                onClick={() => dispatch(addTestCity())}
                className="inline-flex items-center gap-1.5 rounded-xl border border-dashed border-indigo-300 px-3.5 py-2.5 text-xs font-semibold text-indigo-600 transition hover:bg-indigo-50 w-full justify-center"
              >
                <Plus className="h-4 w-4" /> Add Another City Menu
              </button>
            )}
          </div>
        </SectionCard>

        {/* Bank Details */}
        <SectionCard title="Bank Details">
          <Field label="Bank Name" required>
            <TextInput
              placeholder="Enter bank name"
              value={form.bank.bankName}
              disabled={readOnly}
              onChange={(e) => dispatch(setBankField({ field: "bankName", value: e.target.value }))}
            />
          </Field>
          <Field label="Account Holder Name" required>
            <TextInput
              placeholder="Enter account holder name"
              value={form.bank.accountName}
              disabled={readOnly}
              onChange={(e) => dispatch(setBankField({ field: "accountName", value: e.target.value }))}
            />
          </Field>
          <Field label="Account Number" required>
            <TextInput
              placeholder="Enter account number"
              value={form.bank.accountNumber}
              disabled={readOnly}
              onChange={(e) =>
                dispatch(
                  setBankField({
                    field: "accountNumber",
                    value: e.target.value.replace(/\D/g, ""),
                  })
                )
              }
            />
          </Field>
          <Field label="IFSC Code" required>
            <TextInput
              placeholder="e.g. SBIN0001234"
              maxLength={11}
              value={form.bank.ifscCode}
              disabled={readOnly}
              onChange={(e) =>
                dispatch(
                  setBankField({
                    field: "ifscCode",
                    value: e.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 11),
                  })
                )
              }
            />
          </Field>
          <Field label="Branch Location" required>
            <TextInput
              placeholder="Enter branch city / locality"
              value={form.bank.location}
              disabled={readOnly}
              onChange={(e) => dispatch(setBankField({ field: "location", value: e.target.value }))}
            />
          </Field>
        </SectionCard>

        {/* Additional Notes */}
        <SectionCard title="Additional Notes">
          <Field label="Internal Notes">
            <TextArea
              rows={4}
              placeholder="Add any internal onboarding notes or comments..."
              value={form.notes}
              disabled={readOnly}
              onChange={(e) => dispatch(setNotes(e.target.value))}
            />
          </Field>

          <div className="mt-4 flex items-start gap-4 rounded-xl border border-emerald-300 bg-emerald-50/50 p-4">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-500 text-white shadow-xs">
              <ShieldCheck className="h-4 w-4" />
            </span>
            <div>
              <p className="text-xs font-bold text-emerald-900">Verification &amp; Compliance</p>
              <p className="mt-1 text-[11px] leading-relaxed text-emerald-800">
                Ensure mandatory fields are filled accurately. All data is automatically synchronized with the diagnostic backend.
              </p>
            </div>
          </div>
        </SectionCard>

        {/* Section 8: Approval History */}
        {(approvalHistory.length > 0 || isReviewMode || isEditExistingMode || readOnly) && (
          <div className="mt-6 mb-16">
            <ApprovalHistoryTable
              history={approvalHistory}
              centerCode={centerCode || searchParams.get("centerCode") || ""}
            />
          </div>
        )}
      </div>

      {/* Floating Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-slate-300 bg-white/95 px-6 py-4 backdrop-blur-md shadow-lg">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
            <span>
              {readOnly
                ? "View / Review Mode"
                : centerCode
                ? `Editing Facility: ${centerCode}`
                : draftKey
                ? `Server Draft Active (${draftKey.slice(0, 8)}...)`
                : "Auto-saving locally"}
            </span>
          </div>

          <div className="flex items-center gap-3">
            {/* Review Mode (Approvers) */}
            {isReviewMode && (
              <>
                {onExit && (
                  <button
                    type="button"
                    onClick={onExit}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-xs font-bold text-slate-700 hover:bg-slate-50 transition"
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="button"
                  onClick={onReject}
                  disabled={rejecting || approving}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-rose-200 bg-rose-50 px-5 py-2.5 text-xs font-bold text-rose-600 hover:bg-rose-100 transition disabled:opacity-50"
                >
                  {rejecting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
                  Reject Lab
                </button>
                <button
                  type="button"
                  onClick={onApprove}
                  disabled={approving || rejecting}
                  className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-6 py-2.5 text-xs font-bold text-white shadow-md transition hover:bg-emerald-700 disabled:opacity-50"
                >
                  {approving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                  Approve Lab
                </button>
              </>
            )}

            {/* Edit Existing Facility Mode */}
            {isEditExistingMode && (
              <>
                {onExit && (
                  <button
                    type="button"
                    onClick={onExit}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-xs font-bold text-slate-700 hover:bg-slate-50 transition shadow-2xs"
                  >
                    <ChevronLeft className="h-4 w-4" /> Back to List
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleUpdateLab}
                  disabled={isUpdating || isResubmitting}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-indigo-300 bg-indigo-50/70 px-5 py-2.5 text-xs font-bold text-indigo-700 hover:bg-indigo-100 transition shadow-2xs disabled:opacity-50"
                >
                  {isUpdating ? <Loader2 className="h-3.5 w-3.5 animate-spin text-indigo-600" /> : <Save className="h-3.5 w-3.5 text-indigo-600" />}
                  Save Changes
                </button>
                <button
                  type="button"
                  onClick={handleResubmitLab}
                  disabled={isResubmitting || isUpdating}
                  className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-6 py-2.5 text-xs font-bold text-white shadow-md transition hover:bg-indigo-700 disabled:opacity-50"
                >
                  {isResubmitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                  Resubmit for Review
                </button>
              </>
            )}

            {/* Read-Only View Mode */}
            {readOnly && !isReviewMode && onExit && (
              <button
                type="button"
                onClick={onExit}
                className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-xs font-bold text-slate-700 hover:bg-slate-50 transition"
              >
                <ChevronLeft className="h-4 w-4" /> Back to List
              </button>
            )}

            {/* Creator / New Draft Mode */}
            {!isReviewMode && !isEditExistingMode && !readOnly && (
              <>
                {onExit && (
                  <button
                    type="button"
                    onClick={onExit}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-xs font-bold text-slate-700 hover:bg-slate-50 transition shadow-2xs"
                  >
                    <ChevronLeft className="h-4 w-4" /> Back to List
                  </button>
                )}

                <button
                  type="button"
                  onClick={handleResetForm}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-xs font-bold text-slate-700 hover:bg-slate-50 transition shadow-2xs"
                >
                  <RotateCcw className="h-3.5 w-3.5 text-slate-500" /> Reset
                </button>

                <button
                  type="button"
                  onClick={handleSaveDraft}
                  disabled={status === "saving" || status === "submitting"}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-indigo-300 bg-indigo-50/70 px-5 py-2.5 text-xs font-bold text-indigo-700 hover:bg-indigo-100 transition shadow-2xs disabled:opacity-50"
                >
                  {status === "saving" ? <Loader2 className="h-4 w-4 animate-spin text-indigo-600" /> : <Save className="h-4 w-4 text-indigo-600" />}
                  {status === "saving" ? "Saving..." : "Save Draft"}
                </button>

                <button
                  type="button"
                  onClick={handleSubmitReview}
                  disabled={status === "submitting" || status === "saving"}
                  className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-6 py-2.5 text-xs font-bold text-white shadow-md transition hover:bg-indigo-700 disabled:opacity-50"
                >
                  {status === "submitting" ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" /> Submit for Review
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Document Preview Modal */}
      {previewDoc && <DocumentPreviewModal doc={previewDoc} onClose={() => setPreviewDoc(null)} />}
    </div>
  );
}
